import { ApiError, errorMessage } from './errors';

/** Friendly copy for `details.reason` values documented in ARCHITECTURE §17. */
export function inviteErrorMessage(e: unknown, name: string): string {
  if (e instanceof ApiError) {
    const r = e.details.reason;
    if (r === 'offline') return `${name} isn’t around right now.`;
    if (r === 'busy') return `${name} is busy right now.`;
    if (r === 'invites_off') return `${name} has rematch invites turned off.`;
    if (r === 'in_session') return 'Finish your current call first.';
    if (r === 'peer_in_session') return `${name} is in a call right now.`;
    if (r === 'expired') return 'That invite expired.';
    if (e.code === 'conflict') return `You’ve already invited ${name}. Hang tight.`;
    if (e.code === 'rate_limited') return 'You’ve sent a lot of invites. Try again later.';
  }
  return errorMessage(e);
}

export function dropJoinErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.details.reason === 'full') return 'This Drop is full. Catch the next one!';
    const st = e.details.status;
    if (st === 'scheduled') return 'This Drop hasn’t started yet.';
    if (st === 'ended') return 'This Drop has ended.';
    if (st === 'cancelled') return 'This Drop was cancelled.';
  }
  return errorMessage(e);
}

function retryDate(e: ApiError): string | null {
  const at = e.details.retry_at;
  if (typeof at !== 'string') return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

/** SPEC_V2 §2 cooldowns for gender (30 d) and country (7 d). */
export function profileErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const r = e.details.reason;
    const when = retryDate(e);
    if (r === 'gender_change_cooldown') return when ? `You can change this again on ${when}. Gender can be changed once every 30 days.` : 'Gender can be changed once every 30 days.';
    if (r === 'country_change_cooldown') return when ? `You can change your country again on ${when}. It can be changed once a week.` : 'Country can be changed once a week.';
    if (e.code === 'plus_required') return 'Filters are part of Popin Plus.';
  }
  return errorMessage(e);
}

export interface FriendErrorCopy {
  title: string;
  body: string;
  /** Show a "Get Plus" upsell (daily allowance reached on the free plan). */
  upsell?: boolean;
}

/**
 * SPEC_V2 §5 constraints. `declined_recently` is deliberately generic —
 * the sender must never learn they were declined.
 */
export function friendRequestError(e: unknown, name: string, isPlus: boolean): FriendErrorCopy {
  if (e instanceof ApiError) {
    const r = e.details.reason;
    if (r === 'declined_recently') return { title: 'Can’t send a request right now', body: 'Try again another time.' };
    if (r === 'friend_limit') return { title: 'Friend limit reached', body: 'You (or they) already have 500 friends — the most Popin allows.' };
    if (r === 'unavailable') return { title: 'Can’t send a request', body: `${name} isn’t available right now.` };
    if (r === 'already_requested') return { title: 'Already requested', body: `You’ve already sent ${name} a request.` };
    if (r === 'expired') return { title: 'That request expired', body: 'Requests expire after 14 days.' };
    if (r === 'birthdate_required' || r === 'not_onboarded') return { title: 'Finish setting up first', body: 'Taking you back to finish your profile.' };
    if (r === 'pending_limit' || r === 'too_many_pending') return { title: 'Too many pending requests', body: 'You have 20 requests waiting. Cancel some or wait for answers first.' };
    if (r === 'not_met' || r === 'not_met_recently') return { title: 'Can’t send a request', body: `You can only add people you’ve met in the last 7 days.` };
    if (r === 'already_friends') return { title: `You’re already friends with ${name}`, body: '' };
    if (e.code === 'rate_limited' || r === 'daily_limit') {
      return isPlus
        ? { title: 'Daily limit reached', body: 'You’ve sent 30 friend requests today. Try again tomorrow.' }
        : { title: 'Daily limit reached', body: 'Free accounts can send 10 friend requests a day. Plus raises it to 30.', upsell: true };
    }
    if (e.code === 'conflict') return { title: 'Already requested', body: `You’ve already sent ${name} a request.` };
  }
  return { title: 'Request not sent', body: errorMessage(e) };
}

/** SPEC_V2 §6 chat send errors, shown inline under the composer. */
export function chatErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const r = e.details.reason;
    if (r === 'links_locked') return 'Links unlock once you’ve been friends for 24 hours.';
    if (r === 'duplicate') return 'You’ve sent that same message a few times already.';
    if (r === 'unavailable') return 'They can’t receive messages right now.';
    if (r === 'unsend_window') return 'Messages can only be unsent for 10 minutes.';
    if (r === 'birthdate_required') return 'Finish setting up your profile to chat.';
    if (e.code === 'rate_limited') return e.retryAfter ? `You’re sending messages fast. Try again in ${e.retryAfter}s.` : 'You’re sending messages fast. Take a breather.';
    if (e.code === 'account_suspended') return 'You can read messages but can’t send while your account is suspended.';
    if (e.code === 'validation_failed') return e.message || 'Messages can be 1–1000 characters of plain text.';
    if (e.status === 404) return 'This conversation isn’t available anymore.';
  }
  return errorMessage(e, 'Couldn’t send. Check your connection.');
}

/** SPEC_V3 §9 chat challenge errors. */
export function challengeErrorMessage(e: unknown, name: string): string {
  if (e instanceof ApiError) {
    const r = e.details.reason;
    if (r === 'challenge_open') return `You already have a challenge waiting for ${name}. Let them play it first.`;
    if (r === 'daily_limit' || e.code === 'rate_limited') return 'That’s plenty of challenges for today. More tomorrow.';
    if (r === 'unavailable' || e.status === 404) return `${name} can’t get challenges right now.`;
    if (r === 'challenge') return 'Challenges can’t be unsent.';
    if (r === 'not_open' || r === 'resolved') return 'This challenge is already finished.';
    if (r === 'expired') return 'This challenge expired.';
    if (e.code === 'validation_failed') return e.message || 'That challenge isn’t quite right. Try again.';
  }
  return errorMessage(e, 'Couldn’t send. Try again.');
}
